import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export function FaqsSection() {
  return (
    <div className="mx-auto w-full max-w-3xl space-y-7 px-4 pt-4 pb-16">
      <div className="space-y-2 text-center sm:text-left">
        <p className="text-xs font-semibold tracking-[0.18em] uppercase text-amber-500">
          Official Run · July 2026 – December 2026
        </p>
        <h2 className="font-cinzel text-3xl font-bold text-gray-100 md:text-4xl">
          Season 1 Player Handbook
        </h2>
        <p className="max-w-2xl text-gray-400">
          The Pauper League PH (PLPH) Season 1 rule book — season roadmap,
          leaderboard scoring, event tiers, prizes, affiliates, Invitational
          rules, and registration requirements.
        </p>
      </div>

      <Accordion
        type="single"
        collapsible
        className="w-full -space-y-px rounded-lg border border-gray-800 bg-gray-900/60"
        defaultValue="item-1"
      >
        {questions.map((item) => (
          <AccordionItem
            value={item.id}
            key={item.id}
            className="relative border-x border-gray-800 first:rounded-t-lg first:border-t last:rounded-b-lg last:border-b"
          >
            <AccordionTrigger className="px-4 py-4 text-left text-[15px] leading-6 text-gray-100 hover:text-amber-300 hover:no-underline">
              {item.title}
            </AccordionTrigger>
            <AccordionContent className="space-y-3 px-4 pb-4 text-gray-400 leading-relaxed">
              {item.content}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <p className="text-center text-sm text-gray-500 sm:text-left">
        Can&apos;t find what you&apos;re looking for? Reach out to a PLPH
        organizer or your host LGS.
      </p>
    </div>
  );
}

function BulletList({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="list-disc space-y-2 pl-5">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

const questions: { id: string; title: string; content: React.ReactNode }[] = [
  {
    id: "item-1",
    title: "1. The Season 1 Roadmap (June – Dec 2026)",
    content: (
      <>
        <p>
          Our calendar is strictly aligned with the global Lega Pauper Italia
          (LPI) structure so local champions are positioned for international
          play.
        </p>
        <BulletList
          items={[
            <>
              <strong className="text-gray-200">Phase 1: Pre-Season (July 26)</strong>{" "}
              — The warm-up. No official LPI points. Kickoff at Infinity Hobbies
              on Sunday, July 26, 2026. Non-Sanctioned.
            </>,
            <>
              <strong className="text-gray-200">Phase 2: The Active Grind (August – November)</strong>{" "}
              — Leaderboard goes live. From Saturday, August 8 through November
              28, 2026, players battle across exactly{" "}
              <strong className="text-amber-300">34 events</strong> to accumulate
              best scores.
            </>,
            <>
              <strong className="text-gray-200">Phase 3: The Invitational (November 29)</strong>{" "}
              — Top 16 highest-scoring players face off for the Season 1
              Invitational on Sunday, November 29, 2026.
            </>,
            <>
              <strong className="text-gray-200">Phase 4: The International Stop (December 5–6)</strong>{" "}
              — Locally co-hosted International Pauper Tour (IPT). Non-Sanctioned.
            </>,
            <>
              <strong className="text-gray-200">Phase 5: Holiday Break (December 7 onward)</strong>{" "}
              — League goes dark. Rest, brew, and prepare for Season 2.
            </>,
          ]}
        />
      </>
    ),
  },
  {
    id: "item-2",
    title: '2. The Leaderboard: The "Best 20" Cap',
    content: (
      <>
        <p>
          Your journey to the Season 1 Invitational is tracked on the global
          season leaderboard with a capped scoring system that protects players
          from burnout.
        </p>
        <BulletList
          items={[
            <>
              <strong className="text-amber-300">The &quot;Best 20&quot; Rule:</strong>{" "}
              There are 34 events, but final standing uses only your{" "}
              <strong className="text-gray-200">highest-scoring 20</strong>{" "}
              tournament results.
            </>,
            <>
              <strong className="text-gray-200">The Drop Mechanic:</strong> Play
              as many events as you want. From your 21st event onward, your
              lowest-scoring finish is automatically dropped and replaced by a
              higher score.
            </>,
            <>
              <strong className="text-gray-200">Earn Your Keep:</strong> Points
              are strictly performance-based by final standings. No free points
              for paying the entry fee.
            </>,
          ]}
        />
      </>
    ),
  },
  {
    id: "item-3",
    title: "3. Event Tiers, Round Caps & Point Payouts",
    content: (
      <>
        <p>
          The active season features a strict{" "}
          <strong className="text-amber-300">2-Event Weekly Limit</strong>.
          Standard weeks use Friday/Sunday pacing; Premium weeks use
          Saturday/Sunday. Across 17 weeks: exactly{" "}
          <strong className="text-gray-200">34 Events</strong>.
        </p>
        <p>
          Season Leaderboard Points are a strict{" "}
          <strong className="text-gray-200">1:1 ratio</strong> with final
          Companion App Match Points after Swiss:
        </p>
        <BulletList
          items={[
            <>Win = 3 Points</>,
            <>Draw = 1 Point</>,
            <>Loss = 0 Points</>,
          ]}
        />
        <p className="pt-1 font-medium text-gray-300">The Event Tiers & Caps:</p>
        <BulletList
          items={[
            <>
              <strong className="text-gray-200">Regular Tier (14 Events):</strong>{" "}
              Capped at 4 Swiss Rounds. Max leaderboard points: 12.
            </>,
            <>
              <strong className="text-gray-200">Minor Tier (17 Events):</strong>{" "}
              32-player cap. Capped at 5 Swiss Rounds. Max: 15 points.
            </>,
            <>
              <strong className="text-gray-200">Major Tier (3 Events):</strong>{" "}
              82-player cap tentpoles on September 13, October 11, and November
              8. Exactly 6 Swiss Rounds. Max: 18 points.
            </>,
          ]}
        />
        <div className="rounded-lg border border-amber-800/40 bg-amber-900/15 p-3 text-amber-100/90">
          <p className="font-semibold text-amber-300">
            Critical Rule: Swiss Only Point Accumulation
          </p>
          <p className="mt-1">
            Only Match Points earned during Swiss count toward the Season
            Leaderboard. Top 8 playoff wins do{" "}
            <em>not</em> add to your Best 20 accumulation.
          </p>
        </div>
      </>
    ),
  },
  {
    id: "item-4",
    title: "4. Entry Fees & Prize Structure",
    content: (
      <>
        <BulletList
          items={[
            <>
              <strong className="text-gray-200">Regular Tier:</strong> ₱400 Entry
              Fee
            </>,
            <>
              <strong className="text-gray-200">Minor Tier:</strong> ₱600 Entry Fee
            </>,
            <>
              <strong className="text-gray-200">Major Tier:</strong> ₱800 Entry Fee
            </>,
          ]}
        />
        <p className="pt-1 font-medium text-gray-300">
          Prize Support & Revenue Sharing:
        </p>
        <BulletList
          items={[
            <>
              <strong className="text-gray-200">Scaling Prizes:</strong> Base
              prizing guarantees single booster packs for Top 8, scaling up to 3
              booster packs plus a Foil Promo Pack for the Champion.
            </>,
            <>
              <strong className="text-gray-200">Store Credit Rule:</strong> Players
              who do not reach booster-pack brackets receive{" "}
              <strong className="text-amber-300">₱100 in store credit</strong>.
            </>,
            <>
              <strong className="text-gray-200">LGS & League Support:</strong> A
              flat <strong className="text-amber-300">20% PLPH Sanction Fee</strong>{" "}
              is deducted from the gross entry pool. The remaining{" "}
              <strong className="text-gray-200">80%</strong> is split between the
              physical prize pool and the hosting LGS.
            </>,
          ]}
        />
      </>
    ),
  },
  {
    id: "item-5",
    title: "5. PLPH Packet Tournaments",
    content: (
      <>
        <p>
          Affiliate venues may run casual{" "}
          <strong className="text-gray-200">&quot;PLPH Packet Tournaments&quot;</strong>{" "}
          between August and November 2026.
        </p>
        <BulletList
          items={[
            <>
              <strong className="text-gray-200">Purpose:</strong> Casual
              playtesting of the current Pauper meta and localized store revenue.
            </>,
            <>
              <strong className="text-gray-200">Entry Fee:</strong> ₱100.00 to
              ₱300.00 (prize pools at LGS discretion).
            </>,
            <>
              <strong className="text-gray-200">Constraints:</strong> Non-sanctioned;
              do not affect LPI leaderboard points. Max 10 events per store per
              season. Must not conflict with Official Sanctioned Master Schedule
              dates. Stores may run Packet Tournaments simultaneously.
            </>,
          ]}
        />
      </>
    ),
  },
  {
    id: "item-6",
    title: "6. The Battlegrounds: Our LGS Affiliates",
    content: (
      <>
        <p>
          PLPH events are hosted exclusively at recognized partner stores.
          Official Season 1 Affiliates:
        </p>
        <BulletList
          items={[
            "Infinity Hobbies",
            "Collab",
            "D6 Games",
            "Card Pro",
            "Mana Underground",
            "Jace Collection",
            "Green Gate Hobbies",
          ]}
        />
      </>
    ),
  },
  {
    id: "item-7",
    title: "7. Season 1 Invitational & LPI Rewards",
    content: (
      <>
        <p>
          At the end of November, the{" "}
          <strong className="text-amber-300">Top 16</strong> highest-scoring
          players on the Best 20 leaderboard are invited to the Season 1
          Invitational.
        </p>
        <BulletList
          items={[
            <>
              <strong className="text-gray-200">Prize Pool:</strong> Massive base
              prize pool that grows with additional official league sponsors.
            </>,
            <>
              <strong className="text-gray-200">Knockout Format:</strong> 4-Round
              Swiss among 16 qualifiers, cutting to Top 8 Single-Elimination.
            </>,
            <>
              <strong className="text-gray-200">Play/Draw Rule:</strong> In Round
              1, the higher-seeded player chooses to play or draw. Later rounds:
              starting player decided randomly.
            </>,
            <>
              <strong className="text-gray-200">LPI Byes & Rewards:</strong> Byes
              for international Paupergeddon events unlock based on average
              attendance of our 7 largest Legs (16+ → 1 Bye; 32+ → 2 Byes; 64+ →
              3 Byes).
            </>,
          ]}
        />
        <p className="pt-1 font-medium text-gray-300">
          Top 16 Tiebreaker Hierarchy (applied in order):
        </p>
        <BulletList
          items={[
            <>
              <strong className="text-gray-200">1. Total Season Match Points</strong>{" "}
              — Best 20 cap temporarily removed; most Match Points across all
              events attended wins.
            </>,
            <>
              <strong className="text-gray-200">2. Highest Tier Performance</strong>{" "}
              — Highest total points earned at Major and Minor Tier events.
            </>,
            <>
              <strong className="text-gray-200">3. Most Tournament Wins</strong> —{" "}
              Most 1st-place finishes, then 2nd-place, and so on.
            </>,
            <>
              <strong className="text-gray-200">4. Wildcard Play-In</strong> —{" "}
              Sudden-death best-of-three on Invitational morning if still tied.
            </>,
          ]}
        />
      </>
    ),
  },
  {
    id: "item-8",
    title: "8. Registration, Enforcement & Preparation",
    content: (
      <>
        <BulletList
          items={[
            <>
              <strong className="text-gray-200">Player Registration:</strong>{" "}
              Register an account on the PLPH Official Website. The email{" "}
              <em>must exactly match</em> your MTG Companion App email.
            </>,
            <>
              <strong className="text-gray-200">No-Show Penalty:</strong> 1st
              offense −1 pt; 2nd −3 pts; 3rd −5 pts; 4th offense = automatic
              disqualification from Season 1.
            </>,
            <>
              <strong className="text-gray-200">Leaderboard Tracking:</strong> All
              official points, match histories, penalties, and standings are
              public on the PLPH website.
            </>,
            <>
              <strong className="text-gray-200">Format:</strong> Standard MTG
              Pauper (commons only, current official MTGO/Tabletop banlist).
            </>,
            <>
              <strong className="text-gray-200">REL:</strong> Regular Tier at{" "}
              <strong className="text-gray-200">Regular REL</strong>. Minor,
              Major, and Season 1 Invitational at{" "}
              <strong className="text-gray-200">Competitive REL</strong>.
            </>,
            <>
              <strong className="text-gray-200">Decklists:</strong> Mandatory only
              for Minor, Major, Season 1 Invitational, and IPT — submitted
              digitally prior to Round 1. Regular Sanctioned events do not
              require decklist submission.
            </>,
          ]}
        />
      </>
    ),
  },
];
